/**
 * ENG-4705: Channel input watchdog.
 *
 * Symptom: an inbound Slack/Telegram/Direct-Chat message lands in the Claude
 * Code TUI input buffer (visible as `❯ <text>` between the input-box rule
 * lines) but the channel server's auto-submit doesn't fire — the text just
 * sits there until something sends Enter manually.
 *
 * The dispatcher pattern landed in ENG-4684 reduces the *frequency* (slow
 * requests get fanned out to a background subagent so the parent's listener
 * turn returns immediately), but doesn't fix the underlying race: when
 * triage decides a request is "fast" and the parent handles it inline, the
 * main turn still occupies the TUI for several seconds, and any channel
 * message that arrives during that window stacks in the input buffer
 * un-submitted.
 *
 * Workaround the ticket itself documents: `tmux send-keys -t <session>
 * Enter`. This watchdog automates that — every poll cycle it captures the
 * pane for each managed claude-code agent, looks for un-submitted text in
 * the input box, and fires Enter once the same text has been sitting there
 * unchanged for STUCK_THRESHOLD_MS.
 *
 * Safety:
 *  - Skip the agent if a tmux client is attached (a human might be typing).
 *  - Require the text to be unchanged for STUCK_THRESHOLD_MS — short windows
 *    avoid racing the channel server's own (eventual) submit.
 *  - Bounded retries per stuck buffer (ENG-6017): up to MAX_ENTER_FIRES
 *    Enters, each spaced by the stuck threshold, then a loud give-up log.
 *    (The original single-shot design is exactly what stranded koda on
 *    2026-06-04: the one Enter went into Claude Code's session-feedback
 *    dialog, the buffer was marked resolved, and an operator Slack DM sat
 *    unsubmitted for 40+ minutes while every health metric stayed green.)
 *  - Dialog-aware (ENG-6017): if a recognised dialog overlays the pane,
 *    dismiss it via sweepDialogs() instead of firing Enter into it. The
 *    Enter budget is not consumed by dialog dismissals. Unknown dialogs
 *    are default-deny — never keyed.
 *  - ENG-6057: fires even while Claude is rendering a spinner. The old
 *    spinner gate is why the kylie 2026-06-05 stuck inputs were never
 *    healed — the agent stayed busy and the gate held on every poll.
 *    Claude Code queues input typed during a running turn, so the Enter
 *    safely submits the queued text; busy-state is a log annotation now.
 *  - ENG-6057: a lone "…" (U+2026) input is a CC render artifact, ignored —
 *    the fleet's entire historical fire count was Enters at this phantom.
 */

import { sweepDialogs, simpleTextHash, type DialogAction } from './claude-dialogs.js';

const STUCK_THRESHOLD_MS = 5_000;
// ENG-6017: how many Enters we'll fire at one unchanged stuck buffer before
// giving up loudly. Each retry waits another stuckThreshold after the last.
const MAX_ENTER_FIRES = 3;
// ENG-6055: the "disturb" heal sequence — type a char, delete it, submit.
// On the kylie 2026-06-05 incident a bare Enter failed to submit TWICE
// (the same keystroke this watchdog fires) while x→BSpace→Enter submitted
// immediately: the TUI input handler was in a state where a standalone CR
// is swallowed but a real keystroke wakes it. Empirical against the current
// Claude Code / tmux pairing (root-cause tracked in ENG-6057), hence the
// `healMode` escape hatch back to bare Enters.
export const DISTURB_HEAL_KEYS = ['x', 'BSpace', 'Enter'] as const;
// One send-keys per key (never batched into one bracketed paste — see
// defaultArmSender in persistent-session.ts) with a small gap so the TUI
// processes each keystroke individually.
export const DISTURB_INTER_KEY_DELAY_MS = 200;

export type WatchdogHealMode = 'disturb' | 'bare';
// ENG-4716: when a tmux client is attached we don't skip the agent
// outright (the original safety-first carve-out swallowed the steady-
// state "monitoring" case operators care about). Instead we widen the
// stable-buffer window so a human typing has more than 5s to finish
// before the watchdog steps in. Active typing changes the buffer hash
// every keystroke and keeps resetting `firstSeenAt`, so this only
// matters when the buffer is *truly* stable.
const ATTACHED_STUCK_THRESHOLD_MS = 15_000;
const INPUT_BOX_DIVIDER = /^[─━]{10,}/;
const PROMPT_PREFIX = '❯ ';

export interface AgentInputState {
  /** Hash of the input-box text observed last poll. */
  lastInputHash: string;
  /** Wall-clock ms when this hash was first seen. */
  firstSeenAt: number;
  /** ENG-6017: how many Enters we've fired at this hash so far. */
  fires: number;
  /** Wall-clock ms of the most recent Enter fire (0 = never). */
  lastFireAt: number;
  /** Have we already emitted the give-up log line for this hash? */
  gaveUpLogged: boolean;
}

export interface WatchdogIo {
  /** Snapshot of the agent's tmux pane (multiline). Empty / null if the session doesn't exist. */
  capturePane: (codeName: string) => string | null;
  /** Whether any tmux client is attached to the session right now. */
  isClientAttached: (codeName: string) => boolean;
  /** Send a single Enter keystroke to the agent's tmux session. */
  sendEnter: (codeName: string) => void;
  /**
   * ENG-6017: send a dialog-dismissal key sequence (one send-keys call per
   * key, `interKeyDelayMs` apart) to the agent's tmux session.
   */
  sendKeys: (codeName: string, keys: readonly string[], interKeyDelayMs: number) => void;
  /** Logger. */
  log: (msg: string) => void;
  /** Current wall-clock ms (injectable for tests). */
  now: () => number;
  /**
   * ENG-6058: persist a give-up signal for the agent's channel servers.
   * Called exactly once per exhausted heal budget (alongside the GIVING UP
   * log); the channel servers' periodic sweeps turn it into a throttled
   * user-facing "please resend" notice for every conversation with an
   * undrained pending-inbound marker. Optional so tests and older callers
   * compose without it.
   */
  signalGiveUp?: (codeName: string) => void;
}

export interface WatchdogConfig {
  /** ms a stuck buffer must persist before we fire Enter. Defaults to STUCK_THRESHOLD_MS. */
  stuckThresholdMs?: number;
  /**
   * ms a stuck buffer must persist before we fire Enter when a tmux
   * client is attached to the session. Higher than `stuckThresholdMs`
   * to give a human typer extra headroom. Defaults to
   * ATTACHED_STUCK_THRESHOLD_MS.
   */
  attachedStuckThresholdMs?: number;
  /**
   * ENG-6017: max Enters fired at one unchanged stuck buffer before the
   * loud give-up log. Defaults to MAX_ENTER_FIRES.
   */
  maxEnterFires?: number;
  /**
   * ENG-6055: heal keystroke escalation. 'disturb' (default) fires a bare
   * Enter on the first attempt, then the x→BSpace→Enter disturb sequence
   * for the remaining budget (a bare Enter is exactly the keystroke the
   * stuck TUI state eats — kylie 2026-06-05). 'bare' keeps every attempt
   * a single Enter (AGT_INPUT_HEAL_MODE=bare escape hatch).
   */
  healMode?: WatchdogHealMode;
}

/**
 * ENG-6055: pick the keystroke(s) for a given fire attempt (1-based).
 * Attempt 1 is always a bare Enter — it's free, and on healthy hiccups it
 * works. From attempt 2 the bare Enter has demonstrably failed once, so
 * 'disturb' mode escalates to the x→BSpace→Enter sequence. Pure and
 * exported for unit tests.
 */
export function selectFireKeys(
  attempt: number,
  healMode: WatchdogHealMode = 'disturb',
): readonly string[] {
  if (healMode === 'disturb' && attempt >= 2) return DISTURB_HEAL_KEYS;
  return ['Enter'];
}

/**
 * Single-agent decision step — pure given the pane text + state. Returns
 * the next state and the action to take: dismiss a dialog, fire Enter,
 * emit the one-shot give-up log, or nothing.
 *
 * Exported for unit testing.
 */
export function decide(
  pane: string,
  prev: AgentInputState | undefined,
  now: number,
  config: WatchdogConfig = {},
): {
  fire: boolean;
  /** ENG-6017: a recognised dialog overlays the pane — dismiss it instead
   * of firing Enter into it. The Enter budget is untouched. */
  dialog?: DialogAction;
  /** ENG-6017: the Enter budget for this hash is exhausted — emit the
   * give-up log exactly once. */
  gaveUp?: boolean;
  next: AgentInputState | undefined;
} {
  const threshold = config.stuckThresholdMs ?? STUCK_THRESHOLD_MS;
  const maxFires = config.maxEnterFires ?? MAX_ENTER_FIRES;

  // ENG-6017: dialogs first. While a recognised dialog overlays the pane,
  // an Enter would land on the dialog (the koda incident: the session-
  // feedback prompt ate the watchdog's single shot). Dismiss it and leave
  // the stuck-input state untouched — next cycle sees the clean pane and
  // the normal Enter logic resumes. Unknown overlays fall through to the
  // standard path (default-deny: we never key what we don't recognise;
  // worst case the bounded Enters fire and the give-up log surfaces it).
  const dialogAction = sweepDialogs(pane);
  if (dialogAction) {
    return { fire: false, dialog: dialogAction, next: prev };
  }

  const inputText = extractInputBoxText(pane);
  if (!inputText) {
    return { fire: false, next: undefined };
  }

  // ENG-6057: a lone "…" (U+2026) in the input box is a Claude Code render
  // artifact, not a stuck message. Fleet audit 2026-06-05: EVERY watchdog
  // fire in history was len=1 hash=2026 (the char code of …) — the watchdog
  // had only ever fired Enters at this phantom, burning retry budgets and
  // give-up counts on it (paige 2026-06-05) while never once firing on a
  // real message. Treat it as an empty box. (Trade-off: a genuine one-char
  // "…" channel message stuck unsubmitted would be ignored — vanishingly
  // rare against a 100%-phantom historical hit rate.)
  if (inputText === '…') {
    return { fire: false, next: undefined };
  }

  // ENG-6057: the spinner gate is gone. It held fire while Claude was
  // actively working — which is exactly why the kylie 2026-06-05 stuck
  // inputs were never healed: the agent stayed busy with a long task, the
  // gate held on ~115 consecutive polls, and the stuck instruction outlived
  // the whole incident. Claude Code queues input typed during a running
  // turn, so an Enter while busy safely submits the queued text — the
  // original gate's own comment called the keystroke "harmless but noisy".
  // We keep isActivelyProcessing() as a log annotation only (see checkOne).

  const hash = simpleTextHash(inputText);
  if (!prev || prev.lastInputHash !== hash) {
    return {
      fire: false,
      next: { lastInputHash: hash, firstSeenAt: now, fires: 0, lastFireAt: 0, gaveUpLogged: false },
    };
  }

  // ENG-6017: bounded retries replace the original single-shot `resolved`
  // flag. An Enter that lands while a dialog is mid-render (or is eaten by
  // a TUI mode hiccup) gets another chance, each spaced by the same stuck
  // threshold; after maxFires the watchdog surfaces a loud give-up line —
  // the signature monitoring hooks onto — instead of silently shrugging.
  if (prev.fires >= maxFires) {
    if (!prev.gaveUpLogged) {
      return { fire: false, gaveUp: true, next: { ...prev, gaveUpLogged: true } };
    }
    return { fire: false, next: prev };
  }

  const sinceLastAttempt = prev.fires === 0 ? now - prev.firstSeenAt : now - prev.lastFireAt;
  if (sinceLastAttempt < threshold) return { fire: false, next: prev };

  return {
    fire: true,
    next: { ...prev, fires: prev.fires + 1, lastFireAt: now },
  };
}

/**
 * Extract the contents of the Claude Code input box from a pane snapshot.
 * Returns null when the input box is empty or absent.
 *
 * The TUI bracketed input area looks like:
 *   ────────── agt-bob ──
 *   ❯ ping repro 2
 *   ──────────────────────
 *
 * We accept any line starting with `❯ ` whose previous non-empty line is a
 * row of `─` characters (the top divider) — that's robust to width changes
 * and to the optional ` agt-<codeName> ` label embedded in the divider.
 *
 * Exported for unit testing.
 */
export function extractInputBoxText(pane: string): string | null {
  const lines = pane.split('\n');
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (!line.startsWith(PROMPT_PREFIX)) continue;
    // Walk back to the most recent non-empty line; it must be a divider row.
    let j = i - 1;
    while (j >= 0 && (lines[j] ?? '').trim() === '') j--;
    if (j < 0) continue;
    if (!INPUT_BOX_DIVIDER.test((lines[j] ?? '').trim())) continue;
    const text = line.slice(PROMPT_PREFIX.length).trim();
    return text.length > 0 ? text : null;
  }
  return null;
}

/**
 * True when the pane shows a live spinner (`✻ Cogitating…`,
 * `✽ Tinkering… (36s · ↓ 2.1k tokens)`, etc.). Past tense forms like
 * `✻ Cogitated for 7s` mean the work has finished and we treat the agent
 * as idle.
 *
 * ENG-6057: annotation-only — this no longer gates the watchdog's fire
 * (that gate is why the kylie stuck inputs were never healed); it tags the
 * fire log with busy=true|false. The glyph class covers the spinner's
 * animation frames (✻ ✽ ✶ ✳ ✢), not just ✻ — the live kylie pane rendered
 * `✽ Tinkering…`, which the single-glyph match read as "no spinner".
 *
 * Exported for unit testing.
 */
const SPINNER_GLYPHS = ['✻', '✽', '✶', '✳', '✢'];

export function isActivelyProcessing(pane: string): boolean {
  // Search bottom-up for the most recent spinner line.
  const lines = pane.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = (lines[i] ?? '').trim();
    if (!SPINNER_GLYPHS.some((g) => line.startsWith(g))) continue;
    // Past tense: "✻ Cogitated for 7s", "✻ Crunched for 51s" — work done.
    if (/\bfor\s+\d+s\s*$/.test(line)) return false;
    // Present participle: "✻ Cogitating…", "✽ Tinkering… (36s · ↓ 2.1k
    // tokens)" — still working. The trailing parenthetical is optional.
    if (/\b\w+ing[…\.]{0,3}(\s*\([^)]*\))?\s*$/i.test(line)) return true;
    // Ambiguous spinner line — don't treat as busy.
    return false;
  }
  return false;
}

/**
 * Run one watchdog pass over the given agents. Stateful: keeps an internal
 * map of per-agent input state across calls.
 */
export function checkChannelInputs(
  codeNames: readonly string[],
  io: WatchdogIo,
  config: WatchdogConfig = {},
  states: Map<string, AgentInputState> = sharedStates,
): void {
  const live = new Set(codeNames);
  for (const codeName of codeNames) {
    try {
      checkOne(codeName, io, config, states);
    } catch (err) {
      io.log(`[channel-input-watchdog] '${codeName}': ${(err as Error).message}`);
    }
  }
  // Drop state for agents that are no longer in scope.
  for (const key of [...states.keys()]) {
    if (!live.has(key)) states.delete(key);
  }
  // Keep give-up counters in sync with live agents as well — a stale entry
  // would mis-attribute an old count if a code name is reused (CodeRabbit
  // on PR #1764).
  for (const key of [...giveUpCounts.keys()]) {
    if (!live.has(key)) giveUpCounts.delete(key);
  }
}

function checkOne(
  codeName: string,
  io: WatchdogIo,
  config: WatchdogConfig,
  states: Map<string, AgentInputState>,
): void {
  const pane = io.capturePane(codeName);
  if (!pane) {
    states.delete(codeName);
    return;
  }

  // ENG-4716: don't skip outright when a client is attached — operators
  // routinely keep a tmux client open just to monitor agents, and the
  // original blanket skip ate the steady-state case. Widen the stable-
  // buffer threshold instead. Active typing changes the buffer hash on
  // every keystroke, so the unchanged-hash gate already covers the
  // "don't fight the human" concern.
  const attached = io.isClientAttached(codeName);
  const effectiveConfig: WatchdogConfig = attached
    ? {
        ...config,
        stuckThresholdMs:
          config.attachedStuckThresholdMs ?? ATTACHED_STUCK_THRESHOLD_MS,
      }
    : config;

  const prev = states.get(codeName);
  const { fire, dialog, gaveUp, next } = decide(pane, prev, io.now(), effectiveConfig);

  if (next === undefined) {
    states.delete(codeName);
    // ENG-6055: the input box cleared after at least one fire — the heal
    // landed (or a human submitted it). Distinct grep-able success token so
    // healed near-misses are countable separately from GIVING UP events; a
    // rising recovery rate is the early warning that a host is degrading
    // before it starts giving up.
    if (prev && prev.fires > 0) {
      io.log(
        `[channel-input-watchdog] '${codeName}': recovered after ${prev.fires} fire(s) — input submitted (input_hash=${prev.lastInputHash})`,
      );
    }
  } else {
    states.set(codeName, next);
  }

  if (dialog) {
    // ENG-6017: a recognised dialog is blocking the input box — dismiss it
    // instead of firing Enter into it. Next cycle re-evaluates the clean pane.
    io.log(
      `[channel-input-watchdog] '${codeName}': ${dialog.logMessage} (dialog was blocking the input box)`,
    );
    io.sendKeys(codeName, dialog.keys, dialog.interKeyDelayMs);
    return;
  }

  // Log hash + length only — channel input may contain PII / secrets, so
  // prod logging stays hash-only per the project's logging policy.
  const text = extractInputBoxText(pane) ?? '';
  const hash = next?.lastInputHash ?? simpleTextHash(text);

  if (gaveUp) {
    // ENG-6017: loud, single-shot per hash. This line is the fast-detection
    // signature for "input typed but unsubmittable" — the failure mode every
    // upstream health metric is blind to (koda 2026-06-04).
    const maxFires = effectiveConfig.maxEnterFires ?? MAX_ENTER_FIRES;
    io.log(
      `[channel-input-watchdog] '${codeName}': GIVING UP after ${maxFires} Enter attempts — input remains unsubmitted (input_hash=${hash}, len=${text.length})`,
    );
    // Count the event for the responsiveness probe (InputStuckGiveUps
    // metric) so the give-up reaches CloudWatch on the next probe cycle,
    // not just the local log.
    giveUpCounts.set(codeName, (giveUpCounts.get(codeName) ?? 0) + 1);
    // ENG-6058: persist the give-up so the channel servers can tell the
    // affected user(s) to resend — without this, the give-up is operator-
    // facing only and the human keeps talking to a wall.
    try {
      io.signalGiveUp?.(codeName);
    } catch (err) {
      io.log(
        `[channel-input-watchdog] '${codeName}': give-up signal write failed: ${(err as Error).message}`,
      );
    }
    return;
  }

  if (fire) {
    const maxFires = effectiveConfig.maxEnterFires ?? MAX_ENTER_FIRES;
    const attempt = next?.fires ?? 1;
    // ENG-6057: busy-state annotation — the spinner no longer gates the
    // fire (CC queues input typed during a running turn), but knowing
    // whether the agent was mid-turn when the heal landed is diagnostic
    // gold for the eaten-Enter root-cause hunt.
    const busy = isActivelyProcessing(pane);
    // ENG-6055: bare Enter first, disturb sequence (x→BSpace→Enter) for the
    // remaining budget — a bare retry repeats the exact keystroke the stuck
    // TUI state already ate (kylie 2026-06-05). The dialog sweep above has
    // already run this cycle, so the disturb char never lands on a
    // recognised overlay.
    const keys = selectFireKeys(attempt, effectiveConfig.healMode);
    if (keys.length === 1 && keys[0] === 'Enter') {
      io.log(
        `[channel-input-watchdog] '${codeName}': stuck channel input — firing Enter (attempt ${attempt}/${maxFires}, busy=${busy}, input_hash=${hash}, len=${text.length})`,
      );
      io.sendEnter(codeName);
    } else {
      io.log(
        `[channel-input-watchdog] '${codeName}': stuck channel input — escalating to disturb sequence ${keys.join('→')} (attempt ${attempt}/${maxFires}, busy=${busy}, input_hash=${hash}, len=${text.length})`,
      );
      io.sendKeys(codeName, keys, DISTURB_INTER_KEY_DELAY_MS);
    }
  }
}

const sharedStates = new Map<string, AgentInputState>();

// ENG-6017: per-agent count of give-up events since the last responsiveness
// probe drained them. Consumed by the manager's probe cycle and shipped to
// CloudWatch as `InputStuckGiveUps` — the fast-detection metric for the
// "typed but unsubmittable" failure mode (each event means a channel message
// sat in the input box through every bounded Enter retry).
const giveUpCounts = new Map<string, number>();

/**
 * Drain the give-up counter for one agent (returns the count since the last
 * drain). Read-and-reset so each probe cycle reports only new events.
 */
export function takeWatchdogGiveUpCount(codeName: string): number {
  const count = giveUpCounts.get(codeName) ?? 0;
  giveUpCounts.delete(codeName);
  return count;
}

/**
 * Re-credit drained give-up events (ENG-6037): called when the responsiveness
 * probe POST fails after takeWatchdogGiveUpCount() already drained the
 * counter, so the events surface on the next probe cycle instead of being
 * permanently lost. Adds to (not replaces) the live count — give-ups that
 * accrued while the POST was in flight are preserved, and only the
 * undelivered amount is re-added so a later success can't double-count.
 */
export function creditWatchdogGiveUpCount(codeName: string, count: number): void {
  if (count <= 0) return;
  giveUpCounts.set(codeName, (giveUpCounts.get(codeName) ?? 0) + count);
}

/** Test seam — clear the singleton map between tests. */
export function _resetSharedStatesForTests(): void {
  sharedStates.clear();
  giveUpCounts.clear();
}
